import { useMantineTheme } from '@mantine/core';
import * as React from 'react';

import { useDevices } from '@/utils/devices';
import { DESKTOP_MIN_WIDTH_PX } from '@/utils/devices';
import { toPixels } from '@/utils/toPixels';

const getIsLandscape = (): boolean => {
  if (window.matchMedia) {
    return window.matchMedia('(orientation: landscape)').matches;
  }

  return window.innerWidth > window.innerHeight;
};

const isTouchDevice = (): boolean => {
  const hasCoarsePointer = window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : false;

  return hasCoarsePointer || navigator.maxTouchPoints > 0;
};

const getIsPhoneLike = (phonePx: number): boolean => {
  if (!isTouchDevice()) {
    return false;
  }

  const shortestSide = Math.min(window.innerWidth, window.innerHeight);
  const longestSide = Math.max(window.innerWidth, window.innerHeight);

  return shortestSide <= phonePx && longestSide < DESKTOP_MIN_WIDTH_PX;
};

export const useIsPhoneLandscape = (): boolean => {
  const { isPhone } = useDevices();
  const theme = useMantineTheme();
  const phonePx = React.useMemo(() => toPixels(theme.breakpoints.sm), [theme.breakpoints.sm]);
  const [isLandscape, setIsLandscape] = React.useState(getIsLandscape);
  const [isPhoneLike, setIsPhoneLike] = React.useState(() => getIsPhoneLike(phonePx));

  React.useEffect(() => {
    const update = (): void => {
      setIsLandscape(getIsLandscape());
      setIsPhoneLike(getIsPhoneLike(phonePx));
    };

    update();

    const mediaQuery = window.matchMedia ? window.matchMedia('(orientation: landscape)') : null;

    mediaQuery?.addEventListener('change', update);

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return (): void => {
      mediaQuery?.removeEventListener('change', update);

      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [phonePx]);

  return isTouchDevice() && (isPhone || isPhoneLike) && isLandscape;
};
